import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, ArrowUpRight, CheckCircle2, PhoneCall } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Link } from "react-router-dom";
import { SITE } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact-messages`, form);
      setDone(true);
      setForm(initial);
      toast.success("Message sent. We'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#0B0F0E]">
      <Toaster position="top-center" richColors theme="dark" />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-12">
        <div className="overline"><span className="h-line" />Get in touch</div>
        <h1 className="font-display text-[#F4EFE6] text-6xl md:text-7xl lg:text-[5.5rem] mt-7 leading-[0.95] max-w-4xl">
          Begin a conversation with <span className="italic-gold">our team.</span>
        </h1>
        <p className="text-[#9BA39B] text-lg max-w-3xl mt-9 leading-relaxed">
          Whether you have a specific site in mind or simply wish to understand your options, we welcome a confidential conversation.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 bg-[#11171A] border border-[#F4EFE6]/10 p-8 md:p-12">
          {done ? (
            <div data-testid="contact-success" className="py-12 text-center">
              <CheckCircle2 className="text-[#C9A24B] mx-auto" size={48} />
              <h2 className="font-display text-4xl text-[#F4EFE6] mt-6">Message received.</h2>
              <p className="text-[#9BA39B] mt-4">We&apos;ll respond within one working day.</p>
              <button data-testid="contact-reset" onClick={() => setDone(false)} className="usl-btn-outline mt-8">Send Another</button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="space-y-7">
              <div className="grid md:grid-cols-2 gap-7">
                <div>
                  <label className="overline block mb-2">Your name *</label>
                  <input data-testid="contact-name" className="underline-input" value={form.name} onChange={onChange("name")} />
                </div>
                <div>
                  <label className="overline block mb-2">Email *</label>
                  <input data-testid="contact-email" type="email" className="underline-input" value={form.email} onChange={onChange("email")} />
                </div>
                <div>
                  <label className="overline block mb-2">Telephone</label>
                  <input data-testid="contact-phone" className="underline-input" value={form.phone} onChange={onChange("phone")} />
                </div>
                <div>
                  <label className="overline block mb-2">Subject</label>
                  <input data-testid="contact-subject" className="underline-input" value={form.subject} onChange={onChange("subject")} />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Message *</label>
                <textarea data-testid="contact-message" rows={6} className="underline-input resize-none" value={form.message} onChange={onChange("message")} />
              </div>
              <button data-testid="contact-submit" type="submit" disabled={loading} className="usl-btn">
                {loading ? "Sending..." : "Send Message"} <ArrowUpRight size={16} />
              </button>
            </form>
          )}
        </div>

        <aside className="lg:col-span-5 space-y-6">
          <div className="bg-[#C9A24B] text-[#0B0F0E] p-8">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold">Registered Office</div>
            <div className="mt-6 space-y-4 text-[15px]">
              <div className="flex gap-3"><MapPin size={18} className="text-[#0B0F0E] mt-0.5 shrink-0" /><span data-testid="contact-address">{SITE.address}</span></div>
              <div className="flex gap-3"><Mail size={18} className="text-[#0B0F0E] mt-0.5 shrink-0" /><a data-testid="contact-email-link" href={`mailto:${SITE.email}`} className="underline-offset-4 hover:underline">{SITE.email}</a></div>
              <div className="flex gap-3"><Phone size={18} className="text-[#0B0F0E] mt-0.5 shrink-0" /><a data-testid="contact-phone-link" href={`tel:${SITE.phoneIntl}`} className="underline-offset-4 hover:underline">{SITE.phone}</a></div>
            </div>
            <a
              data-testid="contact-map-link"
              href="https://maps.google.com/?q=20+Wenlock+Road+London+N1+7GU"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 mt-7 text-[#0B0F0E] text-xs tracking-[0.22em] uppercase font-semibold link-underline"
            >
              View on Map <ArrowUpRight size={14} />
            </a>
          </div>
          <Link to="/book-a-call" data-testid="contact-book-call" className="block border border-[#C9A24B]/40 bg-[#C9A24B]/5 p-7 hover:bg-[#C9A24B]/10 transition-colors group">
            <div className="overline text-[#C9A24B]"><span className="h-line bg-[#C9A24B]" />Prefer a quick call?</div>
            <div className="flex items-start justify-between gap-4 mt-4">
              <div>
                <div className="font-display text-2xl text-[#F4EFE6] leading-tight">Request a callback<br />at a time that suits you.</div>
                <div className="text-[12px] text-[#9BA39B] mt-3 tracking-[0.18em] uppercase font-semibold flex items-center gap-2 group-hover:text-[#C9A24B] transition-colors">
                  Book a 15-minute conversation <ArrowUpRight size={14} />
                </div>
              </div>
              <PhoneCall className="text-[#C9A24B] flex-shrink-0" size={28} />
            </div>
          </Link>
          <div className="border border-[#F4EFE6]/10 overflow-hidden">
            <iframe
              data-testid="contact-map-iframe"
              title="Upsurge Strategic Land Office"
              src="https://maps.google.com/maps?q=20%20Wenlock%20Road%20London%20N1%207GU&t=&z=15&ie=UTF8&iwloc=&output=embed"
              className="w-full h-[300px] border-0 grayscale brightness-90"
              loading="lazy"
            />
          </div>
        </aside>
      </section>
    </div>
  );
}
